import axios from "axios";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// 120s default — the previous 30s was too tight for two real cases:
//   - GET /rfp/{id}/sections?include_body=true returns multi-MB JSON
//     for tenders with 100+ sections (193-page demo tender hit 23 MB).
//   - GET /rfp/{id}/analysis returns the full analysis blob (~5-10 MB
//     for a complex tender) and BoQ/Bid Matrix panels read from it.
// Most endpoints still return in <1s; this cap only affects the big
// ones. Override per-call via {timeout: ...} when something legitimately
// runs longer (e.g. re-analyse polling already handles its own timing).
export const v2 = axios.create({
  baseURL: `${BASE}/api/v2`,
  timeout: 120000,
});

// Attach the existing v1 token if present so the legacy auth middleware
// stays happy when the same backend mounts both routers behind one origin.
v2.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const v2Api = {
  health: () => v2.get("/health").then((r) => r.data),

  workspace: () => v2.get("/workspace").then((r) => r.data),
  workspaceMembers: () => v2.get("/workspace/members").then((r) => r.data),
  departments: () =>
    v2.get("/departments").then((r) => r.data),
  roles: () => v2.get("/roles").then((r) => r.data),
  users: () => v2.get("/users").then((r) => r.data),
  createUser: (payload) => v2.post("/users", payload).then((r) => r.data),
  patchUser: (id, payload) => v2.patch(`/users/${id}`, payload).then((r) => r.data),
  deleteUser: (id) => v2.delete(`/users/${id}`).then((r) => r.data),
  createDepartment: (payload) =>
    v2.post("/departments", payload).then((r) => r.data),

  catalog: {
    ask: (query, k = 8) =>
      v2.post("/catalog/ask", { query, k }).then((r) => r.data),
    products: () => v2.get("/catalog/products").then((r) => r.data),
    createProduct: (payload) =>
      v2.post("/catalog/products", payload).then((r) => r.data),
    createSku: (productId, payload) =>
      v2.post(`/catalog/products/${productId}/skus`, payload).then((r) => r.data),
    skus: (productId) =>
      v2
        .get("/catalog/skus", { params: productId ? { product_id: productId } : {} })
        .then((r) => r.data),
    match: (description, limit = 5) =>
      v2.get("/catalog/match", { params: { description, limit } }).then((r) => r.data),
    productDetail: (productId) =>
      v2.get(`/catalog/products/${productId}`).then((r) => r.data),
    whereUsed: (productId) =>
      v2.get(`/catalog/products/${productId}/where-used`).then((r) => r.data),
    listSkuRates: (skuId) =>
      v2.get(`/catalog/skus/${skuId}/rates`).then((r) => r.data),
    createSkuRate: (skuId, payload) =>
      v2.post(`/catalog/skus/${skuId}/rates`, payload).then((r) => r.data),
    createCertification: (productId, payload) =>
      v2.post(`/catalog/products/${productId}/certifications`, payload).then((r) => r.data),
    updateCertification: (certId, payload) =>
      v2.patch(`/catalog/certifications/${certId}`, payload).then((r) => r.data),
    deleteCertification: (certId) =>
      v2.delete(`/catalog/certifications/${certId}`).then((r) => r.data),
    createAttribute: (productId, payload) =>
      v2.post(`/catalog/products/${productId}/attributes`, payload).then((r) => r.data),
    updateAttribute: (attrId, payload) =>
      v2.patch(`/catalog/attributes/${attrId}`, payload).then((r) => r.data),
    deleteAttribute: (attrId) =>
      v2.delete(`/catalog/attributes/${attrId}`).then((r) => r.data),
    uploadDocument: (productId, file, { kind = "datasheet", title = "" } = {}) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      if (title) fd.append("title", title);
      return v2
        .post(`/catalog/products/${productId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((r) => r.data);
    },
    documentDownloadUrl: (docId) => `${BASE}/api/v2/catalog/documents/${docId}/download`,
    deleteDocument: (docId) =>
      v2.delete(`/catalog/documents/${docId}`).then((r) => r.data),
  },

  templates: () => v2.get("/templates").then((r) => r.data),
  departments: () => v2.get("/departments").then((r) => r.data),
  departmentLeads: () => v2.get("/department-leads").then((r) => r.data),
  assignDepartmentLead: (departmentCode, userId) =>
    v2.patch(`/department-leads/${departmentCode}`, { user_id: userId || null }).then((r) => r.data),

  projects: {
    list: () => v2.get("/projects").then((r) => r.data),
    get: (id) => v2.get(`/projects/${id}`).then((r) => r.data),
    create: (payload) => v2.post("/projects", payload).then((r) => r.data),
    patch: (id, payload) => v2.patch(`/projects/${id}`, payload).then((r) => r.data),
    bids: (projectId) => v2.get(`/projects/${projectId}/bids`).then((r) => r.data),
    members: (projectId) =>
      v2.get(`/projects/${projectId}/members`).then((r) => r.data),
    addMember: (projectId, payload) =>
      v2.post(`/projects/${projectId}/members`, payload).then((r) => r.data),
    removeMember: (projectId, userId) =>
      v2.delete(`/projects/${projectId}/members/${userId}`).then((r) => r.data),
    markCompleted: (projectId) =>
      v2.post(`/projects/${projectId}/mark-completed`).then((r) => r.data),
    departments: (projectId) =>
      v2.get(`/projects/${projectId}/departments`).then((r) => r.data),
    attachDepartment: (projectId, departmentId) =>
      v2.post(`/projects/${projectId}/departments`, { department_id: departmentId }).then((r) => r.data),
    detachDepartment: (projectId, departmentId) =>
      v2.delete(`/projects/${projectId}/departments/${departmentId}`).then((r) => r.data),
    checklist: (projectId) =>
      v2.get(`/projects/${projectId}/checklist`).then((r) => r.data),
    addChecklistItem: (projectId, payload) =>
      v2.post(`/projects/${projectId}/checklist`, payload).then((r) => r.data),
    regenerateChecklist: (projectId) =>
      v2.post(`/projects/${projectId}/checklist/generate`).then((r) => r.data),
    quotations: (projectId, params = {}) =>
      v2.get(`/projects/${projectId}/quotations`, { params }).then((r) => r.data),
  },

  checklist: {
    patch: (id, payload) => v2.patch(`/checklist/${id}`, payload).then((r) => r.data),
    remove: (id) => v2.delete(`/checklist/${id}`).then((r) => r.data),
  },

  // NOTE: historicalBids is also declared lower as an object literal
  // ({list, create, patch, remove}). JS object-literal duplicate keys
  // silently let the later declaration win, so the function form here
  // was being shadowed → "VI.historicalBids is not a function" at
  // runtime. Removed the function form; HistoricalBidsPage now calls
  // v2Api.historicalBids.list() to match OutcomeCaptureForm's usage of
  // .create(...). Keeping the standalone helpers below — they're used
  // by HistoricalBidsPage's edit/delete + attachment flows and don't
  // collide.
  createHistoricalBid: (payload) => v2.post("/historical-bids", payload).then((r) => r.data),
  patchHistoricalBid: (id, payload) => v2.patch(`/historical-bids/${id}`, payload).then((r) => r.data),
  deleteHistoricalBid: (id) => v2.delete(`/historical-bids/${id}`).then((r) => r.data),
  uploadHistoricalAttachment: (id, kind, file) => {
    const form = new FormData();
    form.append("file", file);
    return v2.post(`/historical-bids/${id}/attachment`, form, {
      params: { kind },
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  historicalAttachmentUrl: (id, kind) =>
    `${v2.defaults.baseURL}/historical-bids/${id}/attachment?kind=${encodeURIComponent(kind)}`,
  deleteHistoricalAttachment: (id, kind) =>
    v2.delete(`/historical-bids/${id}/attachment`, { params: { kind } }).then((r) => r.data),

  dashboard: () => v2.get("/dashboard/stats").then((r) => r.data),
  audit: (params) => v2.get("/audit", { params }).then((r) => r.data),
  reseedDemo: () => v2.post("/admin/reseed-demo").then((r) => r.data),
  diffRfp: (rfpId, against) =>
    v2.get(`/rfp/${rfpId}/diff`, { params: { against } }).then((r) => r.data),

  // Stage 2 PR 4 — native channel_message endpoints (pin / thread).
  channels: {
    // Legacy rfp-scoped endpoints (ChannelMessages component still uses these)
    listMessages: (rfpId, parentId) =>
      v2.get(`/channels/${rfpId}/messages`, {
        params: parentId ? { parent_id: parentId } : {},
      }).then((r) => r.data),
    listPinned: (rfpId) =>
      v2.get(`/channels/${rfpId}/pinned`).then((r) => r.data),
    createMessage: (rfpId, payload) =>
      v2.post(`/channels/${rfpId}/messages`, payload).then((r) => r.data),
    editMessage: (rfpId, messageId, body) =>
      v2.patch(`/channels/${rfpId}/messages/${messageId}`, { body }).then((r) => r.data),
    deleteMessage: (rfpId, messageId) =>
      v2.delete(`/channels/${rfpId}/messages/${messageId}`).then((r) => r.data),
    togglePin: (rfpId, messageId) =>
      v2.post(`/channels/${rfpId}/messages/${messageId}/pin`).then((r) => r.data),
    listMembers: (rfpId) =>
      v2.get(`/channels/${rfpId}/members`).then((r) => r.data),
    addMember: (rfpId, userId, role = "member") =>
      v2.post(`/channels/${rfpId}/members`, { user_id: userId, role }).then((r) => r.data),
    removeMember: (rfpId, userId) =>
      v2.delete(`/channels/${rfpId}/members/${userId}`).then((r) => r.data),
    setMemberPref: (rfpId, userId, payload) =>
      v2.patch(`/channels/${rfpId}/members/${userId}`, payload).then((r) => r.data),

    // New unified channel endpoints
    listUserChannels: () => v2.get(`/user/channels`).then((r) => r.data),
    getMessages: (channelId, parentId) =>
      v2.get(`/channels/${channelId}/messages`, {
        params: parentId ? { parent_id: parentId } : {},
      }).then((r) => r.data),
    sendMessage: (channelId, payload) =>
      v2.post(`/channels/${channelId}/messages`, payload).then((r) => r.data),
    editChannelMessage: (channelId, messageId, body) =>
      v2.patch(`/channels/${channelId}/messages/${messageId}`, { body }).then((r) => r.data),
    deleteChannelMessage: (channelId, messageId) =>
      v2.delete(`/channels/${channelId}/messages/${messageId}`).then((r) => r.data),
    markRead: (channelId) =>
      v2.post(`/channels/${channelId}/read`).then((r) => r.data),
    getUnread: (channelId) =>
      v2.get(`/channels/${channelId}/unread`).then((r) => r.data),
  },

  // Stage 2 PR 6 — direct messages between workspace users.
  dms: {
    listThreads: () => v2.get(`/dms/threads`).then((r) => r.data),
    openThread: (participantIds) =>
      v2.post(`/dms/threads`, { participants: participantIds }).then((r) => r.data),
    listMessages: (threadId) =>
      v2.get(`/dms/threads/${threadId}/messages`).then((r) => r.data),
    sendMessage: (threadId, body) =>
      v2.post(`/dms/threads/${threadId}/messages`, { body }).then((r) => r.data),
  },

  comments: {
    list: (entityType, entityId) =>
      v2.get("/comments", { params: { entity_type: entityType, entity_id: entityId } }).then((r) => r.data),
    create: (payload) => v2.post("/comments", payload).then((r) => r.data),
    patch: (id, payload) => v2.patch(`/comments/${id}`, payload).then((r) => r.data),
    remove: (id) => v2.delete(`/comments/${id}`).then((r) => r.data),
    react: (id, emoji) => v2.post(`/comments/${id}/react`, { emoji }).then((r) => r.data),
  },

  search: (q, limit = 20) =>
    v2.get("/search", { params: { q, limit } }).then((r) => r.data),

  chat: (payload) => v2.post("/chat", payload).then((r) => r.data),

  bidsWorkspaceList: () =>
    v2.get("/bids").then((r) => r.data),

  bids: {
    create: (payload) => v2.post("/bids", payload).then((r) => r.data),
    get: (id) => v2.get(`/bids/${id}`).then((r) => r.data),
    transition: (id, payload) =>
      v2.post(`/bids/${id}/transition`, payload).then((r) => r.data),
    events: (id) => v2.get(`/bids/${id}/events`).then((r) => r.data),
    setArtifactStatus: (id, kind, status) =>
      v2.patch(`/bids/${id}/artifacts/${encodeURIComponent(kind)}`,
        { status }).then((r) => r.data),
    decideArtifactStep: (id, kind, payload) =>
      v2.post(`/bids/${id}/artifacts/${encodeURIComponent(kind)}/decide`, payload)
        .then((r) => r.data),
    myPendingApprovals: () =>
      v2.get("/me/pending-approvals").then((r) => r.data),
  },

  boq: {
    list: (bidId) => v2.get(`/bids/${bidId}/boq`).then((r) => r.data),
    upsert: (bidId, lines) => v2.post(`/bids/${bidId}/boq`, lines).then((r) => r.data),
    // Materialise the bid's BoqLine rows from the parent RFP's analysis
    // + requirements_brief. Same filter rules as BoQPanel so what the
    // user sees matches what the quotation generator consumes.
    syncFromRfp: (bidId, { overwrite = true } = {}) =>
      v2.post(`/bids/${bidId}/boq/sync-from-rfp`, null, { params: { overwrite } })
        .then((r) => r.data),
    match: (lineId, limit = 5) =>
      v2.get(`/boq/${lineId}/match`, { params: { limit } }).then((r) => r.data),
    acceptMatch: (lineId, skuId) =>
      v2.post(`/boq/${lineId}/accept-match/${skuId}`).then((r) => r.data),
    deleteLine: (lineId) =>
      v2.delete(`/boq/${lineId}`).then((r) => r.data),
    catalogSearch: (q, limit = 10) =>
      v2.get("/boq/catalog-search", { params: { q, limit } }).then((r) => r.data),
    bulkRematch: (bidId, params = {}) =>
      v2.post(`/bids/${bidId}/boq/rematch`, null, { params }).then((r) => r.data),
    importPreview: (bidId, file, onUploadProgress) => {
      const form = new FormData();
      form.append("file", file);
      return v2
        .post(`/bids/${bidId}/boq/import`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
          timeout: 120000,
        })
        .then((r) => r.data);
    },
    importCommit: (bidId, payload) =>
      v2.post(`/bids/${bidId}/boq/import/commit`, payload).then((r) => r.data),
  },

  quotations: {
    generate: (payload) => v2.post("/quotations", payload).then((r) => r.data),
    listForBid: (bidId) => v2.get(`/bids/${bidId}/quotations`).then((r) => r.data),
    get: (id) => v2.get(`/quotations/${id}`).then((r) => r.data),
    update: (id, payload) => v2.patch(`/quotations/${id}`, payload).then((r) => r.data),
    setStatus: (id, status, note) => v2.patch(`/quotations/${id}/status`, { status, note: note || null }).then((r) => r.data),
    sendForReview: (id) => v2.post(`/quotations/${id}/send-for-review`).then((r) => r.data),
    downloadUrl: (id, fmt) => `${BASE}/api/v2/quotations/${id}/download/${fmt}`,
    attach: (id, type, file) => {
      const form = new FormData();
      form.append("file", file);
      return v2.post(`/quotations/${id}/attach/${type}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then((r) => r.data);
    },
  },

  winProbability: (payload) => v2.post("/win-probability", payload).then((r) => r.data),

  historical: {
    ask: (query, k = 8) =>
      v2.post("/historical/ask", { query, k }).then((r) => r.data),
    list: (outcome) =>
      v2
        .get("/historical-bids", { params: outcome ? { outcome } : {} })
        .then((r) => r.data),
    similar: (eligibilityText, limit = 3) =>
      v2
        .get("/historical-bids/similar", {
          params: { eligibility_text: eligibilityText, limit },
        })
        .then((r) => r.data),
  },

  approvals: {
    policies: () => v2.get("/approval-policies").then((r) => r.data),
    createPolicy: (payload) => v2.post("/approval-policies", payload).then((r) => r.data),
    patchPolicy: (id, payload) => v2.patch(`/approval-policies/${id}`, payload).then((r) => r.data),
    deletePolicy: (id) => v2.delete(`/approval-policies/${id}`).then((r) => r.data),
    initiate: (payload) => v2.post("/approvals/initiate", payload).then((r) => r.data),
    chain: (chainId) => v2.get(`/approval-chains/${chainId}`).then((r) => r.data),
    decide: (stepId, payload, userId) =>
      v2
        .post(`/approval-steps/${stepId}/decide`, { ...payload, user_id: userId })
        .then((r) => r.data),
    inbox: (roleCode, includeLocked = false) =>
      v2
        .get("/approvals/inbox", {
          params: { role_code: roleCode, include_locked: includeLocked },
        })
        .then((r) => r.data),
  },

  departmentMembers: {
    list: (departmentId) =>
      v2.get(`/departments/${departmentId}/members`).then((r) => r.data),
    add: (departmentId, payload) =>
      v2.post(`/departments/${departmentId}/members`, payload).then((r) => r.data),
    patch: (memberId, payload) =>
      v2.patch(`/department-members/${memberId}`, payload).then((r) => r.data),
    remove: (memberId) =>
      v2.delete(`/department-members/${memberId}`).then((r) => r.data),
    forUser: (userId) =>
      v2.get(`/users/${userId}/departments`).then((r) => r.data),
  },

  feed: {
    list: (limit = 40) => v2.get("/feed", { params: { limit } }).then((r) => r.data),
    stories: (limit = 12) => v2.get("/feed/stories", { params: { limit } }).then((r) => r.data),
    products: (params = {}) => v2.get("/feed/products", { params }).then((r) => r.data),
    stats: () => v2.get("/feed/stats").then((r) => r.data),
    today: (limit = 8) => v2.get("/feed/today", { params: { limit } }).then((r) => r.data),
  },

  pricing: {
    suggest: (params) => v2.get("/pricing/suggest", { params }).then((r) => r.data),
    suggestForLine: (lineId) =>
      v2.get(`/pricing/suggest-for-line/${lineId}`).then((r) => r.data),
    marginAlertForLine: (lineId) =>
      v2.get(`/pricing/margin-alert/${lineId}`).then((r) => r.data),
    benchmarks: () => v2.get("/pricing/benchmarks").then((r) => r.data),
  },

  competitors: {
    ask: (query, k = 8) =>
      v2.post("/competitors/ask", { query, k }).then((r) => r.data),
    list: () => v2.get("/competitors").then((r) => r.data),
    create: (payload) => v2.post("/competitors", payload).then((r) => r.data),
    patch: (id, payload) => v2.patch(`/competitors/${id}`, payload).then((r) => r.data),
    remove: (id) => v2.delete(`/competitors/${id}`).then((r) => r.data),
    listObservations: (params) =>
      v2.get("/competitor-prices", { params }).then((r) => r.data),
    addObservation: (payload) =>
      v2.post("/competitor-prices", payload).then((r) => r.data),
    patchObservation: (id, payload) =>
      v2.patch(`/competitor-prices/${id}`, payload).then((r) => r.data),
    removeObservation: (id) =>
      v2.delete(`/competitor-prices/${id}`).then((r) => r.data),
    scrapeCsvForSku: (skuId, file) => {
      const form = new FormData();
      form.append("file", file);
      return v2
        .post(`/catalog-skus/${skuId}/competitor-prices/scrape-csv`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        })
        .then((r) => r.data);
    },
    scrapeTemplate: () => v2.get("/competitor-prices/scrape-template").then((r) => r.data),
    scrapeCsv: (file) => {
      const form = new FormData();
      form.append("file", file);
      return v2
        .post("/competitor-prices/scrape-csv", form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        })
        .then((r) => r.data);
    },

    // Phase 4 — Competitor 360°. Mirrors the /api/v2/competitor_detail
    // router shape: child collections under /competitors/{id}/... and
    // global PATCH/DELETE under /competitor-projects, /competitor-products,
    // /competitor-certifications.
    dossier: (id) => v2.get(`/competitors/${id}/dossier`).then((r) => r.data),

    listProjects: (id, params) =>
      v2.get(`/competitors/${id}/projects`, { params }).then((r) => r.data),
    createProject: (id, payload) =>
      v2.post(`/competitors/${id}/projects`, payload).then((r) => r.data),
    patchProject: (projectId, payload) =>
      v2.patch(`/competitors/competitor-projects/${projectId}`, payload).then((r) => r.data),
    removeProject: (projectId) =>
      v2.delete(`/competitors/competitor-projects/${projectId}`).then((r) => r.data),

    listProducts: (id) =>
      v2.get(`/competitors/${id}/products`).then((r) => r.data),
    createProduct: (id, payload) =>
      v2.post(`/competitors/${id}/products`, payload).then((r) => r.data),
    patchProduct: (productId, payload) =>
      v2.patch(`/competitors/competitor-products/${productId}`, payload).then((r) => r.data),
    removeProduct: (productId) =>
      v2.delete(`/competitors/competitor-products/${productId}`).then((r) => r.data),

    listCertifications: (id) =>
      v2.get(`/competitors/${id}/certifications`).then((r) => r.data),
    createCertification: (id, payload) =>
      v2.post(`/competitors/${id}/certifications`, payload).then((r) => r.data),
    patchCertification: (certId, payload) =>
      v2.patch(`/competitors/competitor-certifications/${certId}`, payload).then((r) => r.data),
    removeCertification: (certId) =>
      v2.delete(`/competitors/competitor-certifications/${certId}`).then((r) => r.data),
  },

  targets: {
    list: (only_active = true) =>
      v2.get("/targets", { params: { only_active } }).then((r) => r.data),
    create: (payload) => v2.post("/targets", payload).then((r) => r.data),
    patch: (id, payload) => v2.patch(`/targets/${id}`, payload).then((r) => r.data),
    remove: (id) => v2.delete(`/targets/${id}`).then((r) => r.data),
  },

  artifactApproverRoles: {
    list: () => v2.get("/artifact-approver-roles").then((r) => r.data),
    create: (payload) =>
      v2.post("/artifact-approver-roles", payload).then((r) => r.data),
    patch: (id, payload) =>
      v2.patch(`/artifact-approver-roles/${id}`, payload).then((r) => r.data),
    remove: (id) =>
      v2.delete(`/artifact-approver-roles/${id}`).then((r) => r.data),
  },

  stageArtifactRequirements: {
    list: (projectId = null) =>
      v2.get("/stage-artifact-requirements",
        { params: projectId ? { project_id: projectId } : {} })
        .then((r) => r.data),
    create: (payload) =>
      v2.post("/stage-artifact-requirements", payload).then((r) => r.data),
    patch: (id, payload) =>
      v2.patch(`/stage-artifact-requirements/${id}`, payload).then((r) => r.data),
    remove: (id) =>
      v2.delete(`/stage-artifact-requirements/${id}`).then((r) => r.data),
  },

  workflows: {
    listTemplates: (kind) =>
      v2.get("/workflow-templates", { params: kind ? { kind } : {} }).then((r) => r.data),
    createTemplate: (payload) =>
      v2.post("/workflow-templates", payload).then((r) => r.data),
    patchTemplate: (id, payload) =>
      v2.patch(`/workflow-templates/${id}`, payload).then((r) => r.data),
    deleteTemplate: (id) =>
      v2.delete(`/workflow-templates/${id}`).then((r) => r.data),
    apply: (payload) => v2.post("/workflow-instances", payload).then((r) => r.data),
    instance: (entityType, entityId) =>
      v2.get(`/workflow-instances/${entityType}/${entityId}`).then((r) => r.data),
    advance: (instanceId, toStage) =>
      v2.post(`/workflow-instances/${instanceId}/advance`, { to_stage: toStage }).then((r) => r.data),
    completeSubstage: (instanceId, stageCode) =>
      v2.post(`/workflow-instances/${instanceId}/sub-stages/${stageCode}/complete`).then((r) => r.data),
    timeline: (instanceId) =>
      v2.get(`/workflow-instances/${instanceId}/timeline`).then((r) => r.data),
    runSla: () => v2.post("/workflow-instances/sla/run").then((r) => r.data),
    marketplace: (kind) =>
      v2.get("/workflow-templates/marketplace", { params: kind ? { kind } : {} }).then((r) => r.data),
    cloneFromMarketplace: (templateId, payload = {}) =>
      v2.post(`/workflow-templates/marketplace/${templateId}/clone`, payload).then((r) => r.data),
  },

  marginRules: {
    list: () => v2.get("/margin-rules").then((r) => r.data),
    create: (payload) => v2.post("/margin-rules", payload).then((r) => r.data),
    patch: (id, payload) => v2.patch(`/margin-rules/${id}`, payload).then((r) => r.data),
    remove: (id) => v2.delete(`/margin-rules/${id}`).then((r) => r.data),
  },

  reports: {
    winLoss: () => v2.get("/reports/win-loss").then((r) => r.data),
    boqAccuracy: () => v2.get("/reports/boq-accuracy").then((r) => r.data),
    cycleTime: () => v2.get("/reports/cycle-time").then((r) => r.data),
    exportWinLossUrl: (dimension) =>
      `${BASE}/api/v2/reports/win-loss/export.csv?dimension=${encodeURIComponent(dimension)}`,
    exportBoqUrl: () => `${BASE}/api/v2/reports/boq-accuracy/export.csv`,
    exportCycleUrl: () => `${BASE}/api/v2/reports/cycle-time/export.csv`,
    exportWinLossPdfUrl: () => `${BASE}/api/v2/reports/win-loss/export.pdf`,
    exportBoqPdfUrl: () => `${BASE}/api/v2/reports/boq-accuracy/export.pdf`,
    exportCyclePdfUrl: () => `${BASE}/api/v2/reports/cycle-time/export.pdf`,
    roiQuarterly: (quarter) =>
      v2.get("/reports/roi/quarterly", { params: quarter ? { quarter } : {} }).then((r) => r.data),
    exportRoiPdfUrl: (quarter) =>
      `${BASE}/api/v2/reports/roi/quarterly/export.pdf${quarter ? `?quarter=${encodeURIComponent(quarter)}` : ""}`,
  },

  library: {
    ask: (query, k = 8) =>
      v2.post("/library/ask", { query, k }).then((r) => r.data),
    listProposals: (params) =>
      v2.get("/library/proposals", { params }).then((r) => r.data),
    uploadProposal: (file, fields = {}) => {
      const form = new FormData();
      form.append("file", file);
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") form.append(k, v);
      });
      return v2
        .post("/library/proposals", form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        })
        .then((r) => r.data);
    },
    getProposal: (id) => v2.get(`/library/proposals/${id}`).then((r) => r.data),
    deleteProposal: (id) => v2.delete(`/library/proposals/${id}`).then((r) => r.data),
    proposalDownloadUrl: (id) => `${BASE}/api/v2/library/proposals/${id}/download`,

    listCoverLetters: (params) =>
      v2.get("/library/cover-letters", { params }).then((r) => r.data),
    uploadCoverLetter: (file, fields = {}) => {
      const form = new FormData();
      form.append("file", file);
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") form.append(k, v);
      });
      return v2
        .post("/library/cover-letters", form, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        })
        .then((r) => r.data);
    },
    getCoverLetter: (id) => v2.get(`/library/cover-letters/${id}`).then((r) => r.data),
    deleteCoverLetter: (id) => v2.delete(`/library/cover-letters/${id}`).then((r) => r.data),
    coverLetterDownloadUrl: (id) => `${BASE}/api/v2/library/cover-letters/${id}/download`,
  },

  historicalBids: {
    list: (outcome) =>
      v2.get("/historical-bids", { params: outcome ? { outcome } : {} }).then((r) => r.data),
    create: (payload) => v2.post("/historical-bids", payload).then((r) => r.data),
    patch: (id, payload) =>
      v2.patch(`/historical-bids/${id}`, payload).then((r) => r.data),
    remove: (id) => v2.delete(`/historical-bids/${id}`).then((r) => r.data),
  },

  reminders: {
    list: () => v2.get("/reminders").then((r) => r.data),
    create: (payload) => v2.post("/reminders", payload).then((r) => r.data),
    patch: (id, payload) => v2.patch(`/reminders/${id}`, payload).then((r) => r.data),
    remove: (id) => v2.delete(`/reminders/${id}`).then((r) => r.data),
    snooze: (id, days = 7) =>
      v2.post(`/reminders/${id}/snooze`, null, { params: { days } }).then((r) => r.data),
    run: () => v2.post("/reminders/run").then((r) => r.data),
  },

  imports: {
    historicalTemplate: () => v2.get("/import/historical-bids/template").then((r) => r.data),
    historicalUpload: (file, onUploadProgress) => {
      const form = new FormData();
      form.append("file", file);
      return v2
        .post("/import/historical-bids", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
          timeout: 120000,
        })
        .then((r) => r.data);
    },
    catalogTemplate: () => v2.get("/import/catalog/template").then((r) => r.data),
    historicalQuotationsList: (params) =>
      v2.get("/import/historical-quotations", { params }).then((r) => r.data),
    historicalQuotationUpload: (file, meta = {}, onUploadProgress) => {
      const form = new FormData();
      form.append("file", file);
      Object.entries(meta).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") form.append(k, v);
      });
      return v2
        .post("/import/historical-quotations", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
          timeout: 120000,
        })
        .then((r) => r.data);
    },
    catalogUpload: (file, onUploadProgress) => {
      const form = new FormData();
      form.append("file", file);
      return v2
        .post("/import/catalog", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
          timeout: 120000,
        })
        .then((r) => r.data);
    },
  },

  rfp: {
    // Workspace-wide list (RFPList page after the strangler swap).
    // ``project_id`` is optional; omit to get every RFP across the workspace.
    list: (projectId) =>
      v2
        .get("/rfp/", { params: projectId ? { project_id: projectId } : {} })
        .then((r) => r.data),
    listForProject: (projectId) =>
      v2.get(`/rfp/`, { params: { project_id: projectId } }).then((r) => r.data),
    get: (rfpId) => v2.get(`/rfp/${rfpId}`).then((r) => r.data),
    /** Re-run ONLY the classifier on an already-analysed RFP. Cheap
     *  (no Gemini). Use after Section Taxonomy edits. */
    reclassify: (rfpId) =>
      v2.post(`/rfp/${rfpId}/reclassify`).then((r) => r.data),
    analysis: (rfpId) => v2.get(`/rfp/${rfpId}/analysis`).then((r) => r.data),
    // Proposal — v2 endpoints land at /rfp/{id}/proposal*. The page-
    // level ProposalGenerator wraps these with an adapter that maps the
    // v2 shape ({sections:[{key, title, content, edited}]}) back to the
    // v1 shape its JSX expects ({sections:[{id, suggested_response,
    // status}]}) per memory #8 strangler-pattern adapter.
    getProposal: (rfpId) => v2.get(`/rfp/${rfpId}/proposal`).then((r) => r.data),
    generateProposal: (rfpId) => v2.post(`/rfp/${rfpId}/proposal`).then((r) => r.data),
    patchProposalSection: (rfpId, key, content) =>
      v2.patch(`/rfp/${rfpId}/proposal/sections/${encodeURIComponent(key)}`, { content })
        .then((r) => r.data),
    regenerateProposalSection: (rfpId, key) =>
      v2.post(`/rfp/${rfpId}/proposal/sections/${encodeURIComponent(key)}/regenerate`)
        .then((r) => r.data),
    proposalDocxUrl: (rfpId) =>
      `${v2.defaults.baseURL}/rfp/${rfpId}/proposal.docx`,
    deleteProposal: (rfpId) => v2.delete(`/rfp/${rfpId}/proposal`).then((r) => r.data),
    listProposalVersions: (rfpId) =>
      v2.get(`/rfp/${rfpId}/proposal/versions`).then((r) => r.data),
    getProposalVersion: (rfpId, version) =>
      v2.get(`/rfp/${rfpId}/proposal/versions/${version}`).then((r) => r.data),
    restoreProposalVersion: (rfpId, version) =>
      v2.post(`/rfp/${rfpId}/proposal/versions/${version}/restore`).then((r) => r.data),
    bidSummary: (rfpId) =>
      v2.get(`/rfp/${rfpId}/bid-summary`).then((r) => r.data),
    submissionChecklist: (rfpId) =>
      v2.get(`/rfp/${rfpId}/submission-checklist`).then((r) => r.data),
    patchSubmissionChecklist: (rfpId, payload) =>
      v2.patch(`/rfp/${rfpId}/submission-checklist`, payload).then((r) => r.data),
    submissionChecklistPdfUrl: (rfpId) =>
      `${v2.defaults.baseURL}/rfp/${rfpId}/submission-checklist.pdf`,
    sections: (rfpId, { department, includeBody = false } = {}) =>
      v2.get(`/rfp/${rfpId}/sections`, {
        params: {
          ...(department ? { department } : {}),
          ...(includeBody ? { include_body: true } : {}),
        },
      }).then((r) => r.data),
    sectionsSummary: (rfpId) =>
      v2.get(`/rfp/${rfpId}/sections/summary`).then((r) => r.data),
    pagesRange: (rfpId, startPage, endPage) =>
      v2.get(`/rfp/${rfpId}/pages-range`, {
        params: { start_page: startPage, end_page: endPage },
      }).then((r) => r.data),
    searchWithin: (rfpId, { q, department, limit = 25 } = {}) =>
      v2.get(`/rfp/${rfpId}/search`, {
        params: {
          q,
          ...(department ? { department } : {}),
          limit,
        },
      }).then((r) => r.data),
    listSectionReviews: (rfpId) =>
      v2.get(`/rfp/${rfpId}/section-reviews`).then((r) => r.data),
    upsertSectionReview: (rfpId, sectionIndex, body = {}) =>
      v2.put(
        `/rfp/${rfpId}/section-reviews/${sectionIndex}`,
        body,
      ).then((r) => r.data),
    updateSectionDept: (rfpId, sectionIndex, departmentId) =>
      v2.patch(
        `/rfp/${rfpId}/sections/${sectionIndex}/department`,
        { department_id: departmentId ?? null },
      ).then((r) => r.data),
    reanalyseSection: (rfpId, sectionIndex, { useLlm } = {}) =>
      v2.post(
        `/rfp/${rfpId}/sections/${sectionIndex}/reanalyse`,
        null,
        { params: useLlm == null ? {} : { use_llm: useLlm } },
      ).then((r) => r.data),
    listActionItems: (rfpId, { sectionIndex, status } = {}) =>
      v2.get(`/rfp/${rfpId}/action-items`, {
        params: {
          ...(sectionIndex != null ? { section_index: sectionIndex } : {}),
          ...(status ? { status } : {}),
        },
      }).then((r) => r.data),
    crossValidateSection: (rfpId, sectionIndex, productId) =>
      v2.get(
        `/rfp/${rfpId}/sections/${sectionIndex}/cross-validate`,
        { params: { product_id: productId } },
      ).then((r) => r.data),
    explainSection: (rfpId, sectionIndex, { force = false } = {}) =>
      v2.post(
        `/rfp/${rfpId}/sections/${sectionIndex}/explain`,
        null,
        { params: force ? { force: true } : {} },
      ).then((r) => r.data),
    getOcrReview: (rfpId, { needsReviewOnly = false } = {}) =>
      v2.get(
        `/rfp/${rfpId}/ocr-review`,
        { params: needsReviewOnly ? { needs_review_only: true } : {} },
      ).then((r) => r.data),
    // Lightweight progress poll — FE calls every ~2s while
    // parse_status==PARSING, stops at ANALYZED or FAILED.
    getProgress: (rfpId) =>
      v2.get(`/rfp/${rfpId}/progress`).then((r) => r.data),
    listManualSections: (rfpId) =>
      v2.get(`/rfp/${rfpId}/manual-sections`).then((r) => r.data),
    createManualSection: (rfpId, payload) =>
      v2.post(`/rfp/${rfpId}/manual-sections`, payload).then((r) => r.data),
    patchManualSection: (sectionId, payload) =>
      v2.patch(`/manual-sections/${sectionId}`, payload).then((r) => r.data),
    deleteManualSection: (sectionId) =>
      v2.delete(`/manual-sections/${sectionId}`).then((r) => r.data),
    // Product tags — embedded in SectionOut, but these endpoints let
    // ProductTagger add / remove without re-fetching the whole section list.
    addProductTag: (rfpId, sectionIndex, payload) =>
      v2.post(`/rfp/${rfpId}/sections/${sectionIndex}/product-tags`, payload).then((r) => r.data),
    removeProductTag: (rfpId, sectionIndex, tagId) =>
      v2.delete(`/rfp/${rfpId}/sections/${sectionIndex}/product-tags/${tagId}`).then((r) => r.data),
    addDeadline: (rfpId, payload) =>
      v2.post(`/rfp/${rfpId}/deadlines`, payload).then((r) => r.data),
    patchDeadline: (rfpId, deadlineIndex, payload) =>
      v2.patch(`/rfp/${rfpId}/deadlines/${deadlineIndex}`, payload).then((r) => r.data),
    deleteDeadline: (rfpId, deadlineIndex) =>
      v2.delete(`/rfp/${rfpId}/deadlines/${deadlineIndex}`),
    deleteSection: (rfpId, sectionIndex) =>
      v2.delete(`/rfp/${rfpId}/sections/${sectionIndex}`),
    fileUrl: (rfpId) => `${BASE}/api/v2/rfp/${rfpId}/file`,
    fileToken: (rfpId) => v2.get(`/rfp/${rfpId}/file-token`).then((r) => r.data.token),
    /**
     * Build an authenticated file URL by minting a short-lived token via
     * /file-token and embedding it as ?token=. Use this whenever a link
     * is opened by the browser directly (anchor/img/iframe) — direct
     * navigation can't carry an Authorization header and the v2 auth
     * gate will 401.
     *
     * Returns a Promise<string>. ``page`` is optional and appended as
     * a PDF page anchor (#page=N).
     */
    fileUrlAuthed: async (rfpId, { page } = {}) => {
      const token = await v2Api.rfp.fileToken(rfpId);
      const base = `${BASE}/api/v2/rfp/${rfpId}/file?token=${encodeURIComponent(token)}`;
      return page ? `${base}#page=${page}` : base;
    },
    /** Open the RFP file (PDF) in a new tab with auth attached. */
    openFileInNewTab: async (rfpId, page) => {
      try {
        const url = await v2Api.rfp.fileUrlAuthed(rfpId, { page });
        window.open(url, "_blank", "noreferrer");
      } catch (err) {
        // Bubble so the caller can show a toast — never silently swallow
        // auth failures the way an unsigned <a href> does.
        throw err;
      }
    },
    pageImageUrl: (rfpId, page, token) =>
      `${BASE}/api/v2/rfp/${rfpId}/page/${page}/image?token=${encodeURIComponent(token || "")}`,
    wordBoxes: (rfpId, page, token) =>
      v2.get(`/rfp/${rfpId}/page/${page}/word-boxes`, { params: { token } }).then((r) => r.data),
    upload: (form, onUploadProgress) =>
      v2
        .post("/rfp/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress,
          timeout: 120000,
        })
        .then((r) => r.data),
    analyzeAgain: (rfpId, useLlm) =>
      v2
        .post(`/rfp/${rfpId}/analyze`, null, { params: useLlm == null ? {} : { use_llm: useLlm } })
        .then((r) => r.data),
    reextractSummary: (rfpId) =>
      v2.post(`/rfp/${rfpId}/reextract-summary`).then((r) => r.data),
    populateBid: (payload) => v2.post("/rfp/populate-bid", payload).then((r) => r.data),
    reclassify: (rfpId, payload) =>
      v2.post(`/rfp/${rfpId}/reclassify`, payload).then((r) => r.data),
    overrides: (rfpId) => v2.get(`/rfp/${rfpId}/overrides`).then((r) => r.data),
    deleteOverride: (rfpId, overrideId) =>
      v2.delete(`/rfp/${rfpId}/overrides/${overrideId}`).then((r) => r.data),
    remove: (rfpId) => v2.delete(`/rfp/${rfpId}`).then((r) => r.data),
  },

  rfpSearchPresets: {
    list: ({ includeInactive = false } = {}) =>
      v2.get("/rfp-search-presets", {
        params: includeInactive ? { include_inactive: true } : {},
      }).then((r) => r.data),
    create: (payload) =>
      v2.post("/rfp-search-presets", payload).then((r) => r.data),
    patch: (presetId, payload) =>
      v2.patch(`/rfp-search-presets/${presetId}`, payload).then((r) => r.data),
    remove: (presetId) =>
      v2.delete(`/rfp-search-presets/${presetId}`).then((r) => r.data),
  },

  actionItems: {
    // Per-project create — backs the SectionReviewBar's "+ Action item".
    // Reuses the existing /projects/{id}/checklist POST under the hood so
    // we don't fork the storage. The caller passes rfp_id + section_index
    // so the inbox can deep-link back to the source.
    createForProject: (projectId, payload) =>
      v2.post(`/projects/${projectId}/checklist`, payload).then((r) => r.data),
    patch: (itemId, payload) =>
      v2.patch(`/checklist/${itemId}`, payload).then((r) => r.data),
    remove: (itemId) =>
      v2.delete(`/checklist/${itemId}`).then((r) => r.data),
    inbox: ({ openOnly = true } = {}) =>
      v2.get("/action-items/inbox", {
        params: { open_only: openOnly },
      }).then((r) => r.data),
  },

  subsections: {
    // User-authored child sections under an analysed parent section.
    // Parent is identified by (rfpId, sectionIndex) — same stable
    // handle the section review + action item routing use.
    list: (rfpId, sectionIndex, { includeInactive = false } = {}) =>
      v2.get(
        `/rfp/${rfpId}/sections/${sectionIndex}/subsections`,
        { params: includeInactive ? { include_inactive: true } : {} },
      ).then((r) => r.data),
    create: (rfpId, sectionIndex, payload) =>
      v2.post(
        `/rfp/${rfpId}/sections/${sectionIndex}/subsections`,
        payload,
      ).then((r) => r.data),
    patch: (subsectionId, payload) =>
      v2.patch(`/rfp-subsections/${subsectionId}`, payload).then((r) => r.data),
    remove: (subsectionId) =>
      v2.delete(`/rfp-subsections/${subsectionId}`).then((r) => r.data),
  },

  // S2-7 — per-workspace Gemini daily token budget visibility.
  // BudgetChip polls today every 60s; the PATCH is admin-only and
  // currently unused from the UI (CLI / curl for now).
  budget: {
    today: () => v2.get(`/budget/today`).then((r) => r.data),
    patch: (payload) => v2.patch(`/budget`, payload).then((r) => r.data),
  },

  sectionRegistry: {
    list: () => v2.get("/section-registry").then((r) => r.data),
    create: (payload) => v2.post("/section-registry", payload).then((r) => r.data),
    patch: (id, payload) => v2.patch(`/section-registry/${id}`, payload).then((r) => r.data),
    remove: (id) => v2.delete(`/section-registry/${id}`),
  },

  sectionConfig: {
    list: (projectId) => v2.get(`/projects/${projectId}/section-config`).then((r) => r.data),
    create: (projectId, payload) => v2.post(`/projects/${projectId}/section-config`, payload).then((r) => r.data),
    patch: (projectId, entryId, payload) => v2.patch(`/projects/${projectId}/section-config/${entryId}`, payload).then((r) => r.data),
    remove: (projectId, entryId) => v2.delete(`/projects/${projectId}/section-config/${entryId}`),
    pushToRegistry: (projectId, entryId) => v2.post(`/projects/${projectId}/section-config/${entryId}/push-to-registry`).then((r) => r.data),
  },

  assignments: {
    list: (projectId, rfpId) =>
      v2.get(`/projects/${projectId}/rfps/${rfpId}/assignments`).then((r) => r.data),
    claim: (projectId, rfpId, assignmentId, deptId) =>
      v2.post(`/projects/${projectId}/rfps/${rfpId}/assignments/${assignmentId}/claim`, { dept_id: deptId }).then((r) => r.data),
    update: (projectId, rfpId, assignmentId, payload) =>
      v2.patch(`/projects/${projectId}/rfps/${rfpId}/assignments/${assignmentId}`, payload).then((r) => r.data),
    listReviews: (projectId, rfpId, assignmentId) =>
      v2.get(`/projects/${projectId}/rfps/${rfpId}/assignments/${assignmentId}/reviews`).then((r) => r.data),
    createReview: (projectId, rfpId, assignmentId, payload) =>
      v2.post(`/projects/${projectId}/rfps/${rfpId}/assignments/${assignmentId}/reviews`, payload).then((r) => r.data),
    aiClassify: (projectId, rfpId) =>
      v2.post(`/projects/${projectId}/rfps/${rfpId}/assignments/ai-classify`).then((r) => r.data),
  },

  deptSubmissions: {
    list: (projectId, rfpId) =>
      v2.get(`/projects/${projectId}/rfps/${rfpId}/dept-submissions`).then((r) => r.data),
    create: (projectId, rfpId, deptId, payload) =>
      v2.post(`/projects/${projectId}/rfps/${rfpId}/dept-submissions?dept_id=${deptId}`, payload).then((r) => r.data),
    revise: (projectId, rfpId, submissionId, payload) =>
      v2.patch(`/projects/${projectId}/rfps/${rfpId}/dept-submissions/${submissionId}`, payload).then((r) => r.data),
  },

  briefItems: {
    requirementItems: (rfpId) =>
      v2.get(`/rfp/${rfpId}/requirement-items`).then((r) => r.data),
    syncRequirementItems: (rfpId) =>
      v2.post(`/rfp/${rfpId}/requirement-items/sync`).then((r) => r.data),
    createRequirementItem: (rfpId, payload) =>
      v2.post(`/rfp/${rfpId}/requirement-items`, payload).then((r) => r.data),
    patchRequirementItem: (itemId, payload) =>
      v2.patch(`/requirement-items/${itemId}`, payload).then((r) => r.data),
    deleteRequirementItem: (itemId) =>
      v2.delete(`/requirement-items/${itemId}`),
    complianceItems: (rfpId) =>
      v2.get(`/rfp/${rfpId}/compliance-items`).then((r) => r.data),
    syncComplianceItems: (rfpId) =>
      v2.post(`/rfp/${rfpId}/compliance-items/sync`).then((r) => r.data),
    createComplianceItem: (rfpId, payload) =>
      v2.post(`/rfp/${rfpId}/compliance-items`, payload).then((r) => r.data),
    patchComplianceItem: (itemId, payload) =>
      v2.patch(`/compliance-items/${itemId}`, payload).then((r) => r.data),
    deleteComplianceItem: (itemId) =>
      v2.delete(`/compliance-items/${itemId}`),
    sectionLinks: (rfpId, sectionIndex) =>
      v2.get(`/rfp/${rfpId}/section-links`, {
        params: sectionIndex != null ? { section_index: sectionIndex } : {},
      }).then((r) => r.data),
    createSectionLink: (rfpId, payload) =>
      v2.post(`/rfp/${rfpId}/section-links`, payload).then((r) => r.data),
    deleteSectionLink: (linkId) =>
      v2.delete(`/section-links/${linkId}`).then((r) => r.data),
    reviewProgress: (rfpId) =>
      v2.get(`/rfp/${rfpId}/review-progress`).then((r) => r.data),
    proposalGate: (rfpId) =>
      v2.get(`/rfp/${rfpId}/proposal-gate`).then((r) => r.data),
    generateProposal: (bidId) =>
      v2.post(`/bids/${bidId}/generate-proposal`).then((r) => r.data),
  },
};

export default v2Api;
